/**
 * Sample-interval picker (djin-cloud#806, trimmed in #952).
 *
 * Five pinnable intervals, `1s` to `1d`. The server accepts more — its full
 * auto ladder plus `1s` — and still resolves `auto` onto that ladder, so the
 * short list removes choices, never results.
 *
 * The two FINE intervals are range-capped (`INTERVAL_MAX_SPAN_MS`): `1s` to
 * 1 h because it always reads the raw hypertable, `1m` to 24 h because a
 * pinned minute grain over weeks is the shape that truncates and runs slow.
 * An option that cannot cover the current range is disabled here, and the
 * reducer drops a pinned interval back to `auto` when the range outgrows it —
 * this component only reflects that rule, it does not enforce it.
 *
 * The "resolved to …" badge is shown only while `auto` is the selection.
 * `metadata.interval` is the RESOLVED shorthand of the last query, so beside a
 * pinned `1h` it would attribute the previous auto run's answer to the new
 * choice.
 */

import { Clock } from 'lucide-react';
import { Card } from '@/components/gamma';
import { cn } from '@/utils/cn';
import { useHistorian, rangeSpanMs } from '@/contexts/HistorianContext';
import {
  EXPLICIT_INTERVALS,
  INTERVAL_HINTS,
  intervalAllowsSpan,
  type IntervalOption,
} from './historianConstants';

/**
 * Long-form labels, keyed by the wire shorthand.
 *
 * `Record<IntervalOption, string>` is load-bearing: adding a rung to
 * `EXPLICIT_INTERVALS` without a label here fails the compile instead of
 * rendering `undefined` in the picker.
 */
const LABELS: Record<IntervalOption, string> = {
  auto: 'Auto',
  '1s': '1 second',
  '1m': '1 minute',
  '15m': '15 minutes',
  '1h': '1 hour',
  '1d': '1 day',
};

const OPTIONS: IntervalOption[] = ['auto', ...EXPLICIT_INTERVALS];

export interface IntervalSelectorProps {
  className?: string;
}

export function IntervalSelector({ className }: IntervalSelectorProps) {
  const { state, setInterval } = useHistorian();
  const resolved = state.interval === 'auto' ? state.queryResult?.metadata.interval : undefined;
  const spanMs = rangeSpanMs(state.timeRange);

  return (
    <Card variant="panel" className={className} data-testid="historian-interval-selector">
      <h3 className="flex items-center gap-2 text-ems-text-primary font-semibold text-sm mb-2">
        <Clock size={14} aria-hidden="true" /> Sample Interval
      </h3>

      <div role="radiogroup" aria-label="Sample interval" className="space-y-1">
        {OPTIONS.map((opt) => {
          const active = state.interval === opt;
          const allowed = intervalAllowsSpan(opt, spanMs);
          return (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={!allowed}
              data-testid={`historian-interval-${opt}`}
              onClick={() => setInterval(opt)}
              className={cn(
                'w-full text-left px-3 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-ems-green/30',
                active
                  ? 'border-ems-green bg-ems-green/10'
                  : 'border-ems-border hover:bg-ems-bg-card-hover',
                !allowed && 'opacity-40 cursor-not-allowed hover:bg-transparent'
              )}
            >
              <div className="flex items-center gap-2 text-sm text-ems-text-primary">
                <span>{LABELS[opt]}</span>
                {opt === 'auto' && (
                  <span className="text-[10px] px-1.5 rounded bg-ems-green/20 text-ems-green">
                    Recommended
                  </span>
                )}
                {opt === 'auto' && resolved && (
                  <span className="ml-auto text-xs text-ems-text-muted">resolved to {resolved}</span>
                )}
              </div>
              <div className="text-xs text-ems-text-muted">{INTERVAL_HINTS[opt]}</div>
            </button>
          );
        })}
      </div>

      <p className="mt-2 text-xs text-ems-text-muted">
        Auto picks the finest interval that keeps each series near 1,500 points. An interval
        coarser than the range yields a single bucket.
      </p>
    </Card>
  );
}

export default IntervalSelector;
